import {
  LayoutDashboardIcon,
  ListChecksIcon,
  MessageSquareIcon,
  SettingsIcon,
  TargetIcon,
  TerminalIcon,
  type LucideIcon,
} from "lucide-react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { useHasTool, useTools } from "../../access/AccessProvider";
import { isSettingsPathname } from "../../appRoutes";
import type { OperatorsAuthTool } from "../../operatorsAuthSession";
import {
  useQueuesRailIndicator,
  type RailIndicator,
} from "../../tools/event-queue/useQueuesRailIndicator";
import { cn } from "~/lib/utils";
import { Tooltip, TooltipPopup, TooltipTrigger } from "~/components/ui/tooltip";

import { AppUserMenu } from "./AppUserMenu";

const TOOL_ICON_BY_NAME: Record<string, LucideIcon> = {
  "chart-bar": LayoutDashboardIcon,
  "layout-dashboard": LayoutDashboardIcon,
  "list-checks": ListChecksIcon,
  "message-square": MessageSquareIcon,
  target: TargetIcon,
  terminal: TerminalIcon,
};

type NavItem = {
  id: string;
  label: string;
  icon: ReactNode;
  /** A tool's "something needs you" dot. */
  indicator?: RailIndicator | null;
  isActive: boolean;
  onSelect: () => void;
};

function iconForTool(tool: OperatorsAuthTool): ReactNode {
  const Icon = TOOL_ICON_BY_NAME[tool.icon] ?? TerminalIcon;
  return <Icon />;
}

function isToolPathActive(pathname: string, route: string): boolean {
  return pathname === route || pathname.startsWith(`${route}/`);
}

function AppNavItem({ item }: { item: NavItem }) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            data-active={item.isActive ? "true" : undefined}
            aria-current={item.isActive ? "page" : undefined}
            aria-label={
              item.indicator
                ? `${item.label}, ${item.indicator === "critical" ? "needs attention now" : "needs attention"}`
                : item.label
            }
            className="relative flex size-10 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-active:bg-accent data-active:text-primary [&_svg]:size-5"
            onClick={item.onSelect}
          >
            {item.icon}
            {item.indicator ? (
              <span
                aria-hidden
                data-indicator={item.indicator}
                className={cn(
                  "absolute top-1.5 right-1.5 size-2 rounded-full ring-2 ring-card",
                  item.indicator === "critical" ? "bg-destructive" : "bg-warning",
                )}
              />
            ) : null}
          </button>
        }
      />
      <TooltipPopup side="right" sideOffset={8}>
        {item.label}
      </TooltipPopup>
    </Tooltip>
  );
}

export function AppNavSidebar() {
  const navigate = useNavigate();
  const pathname = useLocation({ select: (loc) => loc.pathname });
  const tools = useTools();
  const queuesIndicator = useQueuesRailIndicator(useHasTool("event-queue"));

  const items: NavItem[] = [
    ...tools.map((tool) => ({
      id: `tool:${tool.slug}`,
      label: tool.slug === "operators" ? "Threads" : tool.name,
      icon: iconForTool(tool),
      indicator: tool.slug === "event-queue" ? queuesIndicator : null,
      isActive: isToolPathActive(pathname, tool.route),
      onSelect: () => {
        void navigate({
          to: tool.route as "/operators" | "/dashboards" | "/okrs" | "/event-queue",
        });
      },
    })),
    {
      id: "settings",
      label: "Settings",
      icon: <SettingsIcon />,
      isActive: isSettingsPathname(pathname),
      onSelect: () => {
        void navigate({ to: "/settings" });
      },
    },
  ];

  return (
    <aside
      data-slot="app-nav-sidebar"
      className="z-40 flex h-full min-h-0 w-11 shrink-0 flex-col border-r border-border bg-card text-foreground sm:w-16"
    >
      <nav className="min-h-0 flex-1 overflow-y-auto py-2" aria-label="App">
        <ul className="flex flex-col items-center gap-1">
          {items.map((item) => (
            <li key={item.id}>
              <AppNavItem item={item} />
            </li>
          ))}
        </ul>
      </nav>
      <div
        className="flex shrink-0 justify-center border-t border-border py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]"
        data-slot="app-nav-footer"
      >
        <AppUserMenu />
      </div>
    </aside>
  );
}
